import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Upload, Card, Row, Col, Radio, InputNumber, Switch, message
} from 'antd';
import {
  UploadOutlined, PlusOutlined, SaveOutlined, CloseOutlined, InboxOutlined, PictureOutlined
} from '@ant-design/icons';
import { createGalleryImage, updateGalleryImage } from '../../../api/galleryApi';

const { TextArea } = Input;

const GalleryForm = ({ galleryImage, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mediaType, setMediaType] = useState('photo');
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (galleryImage) {
      const existingMediaType = galleryImage.mediaType || 'photo';
      setMediaType(existingMediaType);
      
      // Transform file data for Upload component
      if (galleryImage.mediaUrl && existingMediaType === 'photo') {
        setFileList([{
          uid: '-1',
          name: 'Current Image',
          status: 'done',
          url: galleryImage.mediaUrl
        }]);
        setPreviewUrl(galleryImage.mediaUrl);
      }

      form.setFieldsValue({
        title: galleryImage.title || '',
        description: galleryImage.description || '',
        altText: galleryImage.altText || '',
        mediaType: existingMediaType,
        mediaUrl: galleryImage.mediaUrl || '',
        order: galleryImage.order || 0,
        isActive: galleryImage.isActive !== undefined ? galleryImage.isActive : true,
      });
    } else {
      form.resetFields();
      setMediaType('photo');
      setFileList([]);
      setPreviewUrl('');
    }
  }, [galleryImage, form]);

  // Custom upload handler to prevent auto-upload
  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  // Handle file upload changes
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      const url = URL.createObjectURL(newFileList[0].originFileObj);
      setPreviewUrl(url);
    } else if (newFileList.length > 0 && newFileList[0].url) {
      setPreviewUrl(newFileList[0].url);
    } else {
      setPreviewUrl('');
    }
  };

  // Handle media type change
  const handleMediaTypeChange = (e) => {
    setMediaType(e.target.value);
    form.setFieldValue('mediaType', e.target.value);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Append all form fields
      submitData.append('title', values.title);
      submitData.append('description', values.description || '');
      submitData.append('altText', values.altText || '');
      submitData.append('mediaType', values.mediaType);
      submitData.append('order', values.order || 0);
      submitData.append('isActive', values.isActive);

      if (values.mediaType === 'video') {
        submitData.append('mediaUrl', values.mediaUrl);
      } else if (fileList.length > 0 && fileList[0].originFileObj) {
        // Append new file with the correct field name 'image'
        submitData.append('image', fileList[0].originFileObj);
      }

      if (galleryImage) {
        await updateGalleryImage(galleryImage._id, submitData);
        message.success('Gallery item updated successfully');
      } else {
        await createGalleryImage(submitData);
        message.success('Gallery item uploaded successfully');
      }

      form.resetFields();
      setFileList([]);
      setPreviewUrl('');
      onSubmit();
    } catch (error) {
      console.error('Error saving gallery item:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save gallery item';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Shared style object
  const formStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    color: '#000000'
  };

  const headingStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '19px',
    fontWeight: 400,
    color: '#000000',
    marginBottom: '30px',
    backgroundColor: '#e6d5f0',
  };

  return (
    <div style={{
      padding: '0',
      background: '#f0f2f5',
      fontFamily: 'Rubik, sans-serif',
      maxHeight: '90vh',
      overflow: 'auto',
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    }}>
      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <Card variant="borderless" style={{ padding: '16px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          {/* Basic Details */}
          <Card
            type="inner"
            title={<span style={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '19px',
              fontWeight: 400,
              color: '#000000',
              marginBottom: '30px',
            }}>Gallery Media Details</span>}
            styles={{ body: { padding: '16px' } }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={<span style={formStyle}>Title</span>}
                  name="title"
                  rules={[{ required: true, message: 'Please enter a title' }]}
                >
                  <Input placeholder="Enter title" style={formStyle} size="middle" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={formStyle}>Media Type</span>}
                  name="mediaType"
                  initialValue="photo"
                >
                  <Radio.Group onChange={handleMediaTypeChange} style={formStyle}>
                    <Radio value="photo" style={formStyle}>Photo</Radio>
                    <Radio value="video" style={formStyle}>Video (YouTube)</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={<span style={formStyle}>Description</span>}
                  name="description"
                >
                  <TextArea
                    rows={3}
                    placeholder="Enter description"
                    style={formStyle}
                  />
                </Form.Item>
              </Col>
            </Row>

            {mediaType === 'photo' ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={<span style={formStyle}>Image</span>}
                      rules={[{ required: !galleryImage, message: 'Please upload an image' }]}
                    >
                      <Upload
                        listType="picture-card"
                        fileList={fileList}
                        onChange={handleUploadChange}
                        customRequest={customRequest}
                        maxCount={1}
                        accept="image/*"
                        beforeUpload={(file) => {
                          const isImage = file.type.startsWith('image/');
                          if (!isImage) {
                            message.error('You can only upload image files!');
                          }
                          const isLt10M = file.size / 1024 / 1024 < 10;
                          if (!isLt10M) {
                            message.error('Image must be smaller than 10MB!');
                          }
                          return isImage && isLt10M;
                        }}
                      >
                        {fileList.length < 1 && (
                          <div style={formStyle}>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>Upload Image</div>
                          </div>
                        )}
                      </Upload>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={<span style={formStyle}>Alt Text</span>}
                      name="altText"
                    >
                      <Input placeholder="Enter alt text for accessibility" style={formStyle} />
                    </Form.Item>
                  </Col>
                </Row>
                {previewUrl && (
                  <Row gutter={16}>
                    <Col span={24}>
                      <div style={{ marginBottom: '16px' }}>
                        <span style={{ ...formStyle, display: 'block', marginBottom: '8px' }}>Preview:</span>
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          style={{ 
                            maxWidth: '300px', 
                            maxHeight: '300px', 
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #d9d9d9'
                          }} 
                        />
                      </div>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label={<span style={formStyle}>YouTube URL</span>}
                    name="mediaUrl"
                    rules={[
                      { required: mediaType === 'video', message: 'Please enter a YouTube URL' },
                      { type: 'url', message: 'Please enter a valid URL' }
                    ]}
                  >
                    <Input 
                      placeholder="https://www.youtube.com/watch?v=..." 
                      style={formStyle} 
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={<span style={formStyle}>Display Order</span>}
                  name="order"
                  initialValue={0}
                >
                  <InputNumber 
                    min={0} 
                    style={{ ...formStyle, width: '100%' }} 
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={<span style={formStyle}>Active Status</span>}
                  name="isActive"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch 
                    checkedChildren="Active" 
                    unCheckedChildren="Inactive"
                    style={{ backgroundColor: form.getFieldValue('isActive') ? '#470172' : undefined }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Action Buttons */}
          <div style={{ 
            marginTop: '24px', 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px', 
            paddingTop: '16px', 
            borderTop: '1px solid #e8e8e8' 
          }}>
            <Button
              icon={<CloseOutlined />}
              onClick={onCancel}
              disabled={loading}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                color: '#470172',
                borderColor: '#470172',
                height: '36px',
                padding: '0 24px'
              }}
            >
              Cancel
            </Button>
            <Button
              icon={<SaveOutlined />}
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                backgroundColor: '#470172',
                borderColor: '#470172',
                height: '36px',
                padding: '0 24px'
              }}
            >
              {loading ? 'Saving...' : (galleryImage ? 'Update Media' : 'Add Media')}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default GalleryForm;
